import { useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';
import { approveExpense, approveExpenseAsException, rejectExpense, type ExpenseView } from '../expenses.api';
import { decisionToast, type DecisionMode } from '../policies/expense-decision';

export interface DecisionTarget {
  mode: DecisionMode;
  expense: ExpenseView;
}

/** What the drawer collects: a reason for a rejection or an exception, and for an exception the second person who signs it off. */
export interface DecisionValues {
  reason?: string;
  overrideApproverId?: string;
}

const FALLBACK_ERROR = 'Could not record the decision — try again.';

function send(target: DecisionTarget, values: DecisionValues): Promise<ExpenseView> {
  const { mode, expense } = target;
  if (mode === 'approve') return approveExpense(expense.id);
  if (mode === 'reject') return rejectExpense(expense.id, { reason: values.reason ?? '' });
  return approveExpenseAsException(expense.id, { reason: values.reason ?? '', overrideApproverId: values.overrideApproverId ?? '' });
}

/**
 * The decision drawer's state: which expense and which decision it is open for, and the server's refusal shown inline (a form failure is
 * not a toast). The toast is for the server's confirmation. `onDecided` refreshes the queue, so the row leaves it.
 */
export function useExpenseDecision(onDecided: () => Promise<void>) {
  const toast = useToast();
  const [target, setTarget] = useState<DecisionTarget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function open(mode: DecisionMode, expense: ExpenseView) {
    setError(null);
    setTarget({ mode, expense });
  }

  async function decide(values: DecisionValues): Promise<void> {
    if (!target) return;
    setError(null);
    setSubmitting(true);
    try {
      await send(target, values);
      await onDecided();
      toast.success(decisionToast(target.mode, target.expense));
      setTarget(null);
    } catch (err) {
      setError(apiErrorMessage(err) ?? FALLBACK_ERROR);
    } finally {
      setSubmitting(false);
    }
  }

  return { target, error, submitting, open, close: () => setTarget(null), decide };
}
